import { Field, Input, PasswordInput, Select } from '@aibi/ui';
import { MYSQL_SSL_MODES } from './connection-labels';
import { Notice } from './ErrorNotice';
import type { MysqlForm, SqlServerForm } from './form-rules';

type Errors = Partial<Record<string, string>>;

const secretHint = (usable: boolean) =>
  usable
    ? 'Guardada. Dejela vacia para conservarla.'
    : 'Se guarda cifrada en el servidor y nunca se vuelve a mostrar.';

function ServerFields({
  host,
  port,
  database,
  errors,
  hostHint,
  onText,
}: {
  readonly host: string;
  readonly port: string;
  readonly database: string;
  readonly errors: Errors;
  readonly hostHint: string;
  readonly onText: (key: 'host' | 'port' | 'database', value: string) => void;
}) {
  return (
    <>
      <Field label="Servidor" className="sm:col-span-4" error={errors.host} hint={hostHint}>
        {(control) => (
          <Input
            {...control}
            value={host}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => {
              onText('host', event.target.value);
            }}
          />
        )}
      </Field>
      <Field label="Puerto" className="sm:col-span-2" error={errors.port}>
        {(control) => (
          <Input
            {...control}
            value={port}
            inputMode="numeric"
            onChange={(event) => {
              onText('port', event.target.value);
            }}
          />
        )}
      </Field>
      <Field label="Base de datos" className="sm:col-span-3" error={errors.database}>
        {(control) => (
          <Input
            {...control}
            value={database}
            autoComplete="off"
            onChange={(event) => {
              onText('database', event.target.value);
            }}
          />
        )}
      </Field>
    </>
  );
}

const MSSQL_AUTH_OPTIONS = [
  { value: 'sql', label: 'Usuario y contrasena de SQL' },
  { value: 'entra-sp', label: 'Service principal de Entra ID' },
] as const;

/** Formulario SQL Server / Azure SQL; la credencial solo vive en el estado del formulario. */
export function SqlServerFields({
  form,
  errors,
  savedSecretUsable,
  locked,
  onChange,
}: {
  readonly form: SqlServerForm;
  readonly errors: Errors;
  readonly savedSecretUsable: boolean;
  readonly locked: boolean;
  readonly onChange: (form: SqlServerForm) => void;
}) {
  const set = <K extends keyof SqlServerForm>(key: K, value: SqlServerForm[K]) => {
    onChange({ ...form, [key]: value });
  };
  const text = (key: keyof SqlServerForm) => (event: { target: { value: string } }) => {
    onChange({ ...form, [key]: event.target.value });
  };
  const sp = form.authType === 'entra-sp';
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="info">
        Use un usuario con solo el rol db_datareader. Las consultas se ejecutan en transacciones que
        siempre se revierten.
      </Notice>
      <fieldset disabled={locked} className="min-w-0">
        <legend className="sr-only">Datos del servidor</legend>
        <div className="grid gap-3 sm:grid-cols-6">
          <ServerFields
            host={form.host}
            port={form.port}
            database={form.database}
            errors={errors}
            hostHint="Por ejemplo miservidor.database.windows.net"
            onText={(key, value) => {
              set(key, value);
            }}
          />
          <Field
            label="Esquema (opcional)"
            className="sm:col-span-3"
            error={errors.schema}
            hint="Vacio para explorar todos los esquemas visibles."
          >
            {(control) => (
              <Input
                {...control}
                value={form.schema}
                placeholder="dbo"
                autoComplete="off"
                onChange={text('schema')}
              />
            )}
          </Field>
          <Field label="Autenticacion" className="sm:col-span-3" error={errors.authType}>
            {(control) => (
              <Select
                {...control}
                value={form.authType}
                onChange={(event) => {
                  set('authType', event.target.value === 'entra-sp' ? 'entra-sp' : 'sql');
                }}
              >
                {MSSQL_AUTH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {sp ? (
            <>
              <Field
                label="Tenant ID"
                className="sm:col-span-3"
                error={errors.tenantId}
                hint="Directory (tenant) ID de Entra ID."
              >
                {(control) => (
                  <Input
                    {...control}
                    value={form.tenantId}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={text('tenantId')}
                  />
                )}
              </Field>
              <Field
                label="Client ID"
                className="sm:col-span-3"
                error={errors.clientId}
                hint="Application (client) ID del service principal."
              >
                {(control) => (
                  <Input
                    {...control}
                    value={form.clientId}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={text('clientId')}
                  />
                )}
              </Field>
            </>
          ) : (
            <Field label="Usuario" className="sm:col-span-3" error={errors.user}>
              {(control) => (
                <Input
                  {...control}
                  value={form.user}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={text('user')}
                />
              )}
            </Field>
          )}
          <label className="flex items-center gap-2 text-xs sm:col-span-3">
            <input
              type="checkbox"
              checked={form.encrypt}
              onChange={(event) => {
                set('encrypt', event.target.checked);
              }}
            />
            Cifrar la conexion (obligatorio en Azure SQL)
          </label>
          <label className="flex items-center gap-2 text-xs sm:col-span-3">
            <input
              type="checkbox"
              checked={form.trustServerCertificate}
              onChange={(event) => {
                set('trustServerCertificate', event.target.checked);
              }}
            />
            Confiar en el certificado del servidor (solo redes de confianza)
          </label>
        </div>
      </fieldset>
      {sp && (
        <Notice tone="info">
          El service principal debe existir como usuario de la base: CREATE USER [nombre-app] FROM
          EXTERNAL PROVIDER y ALTER ROLE db_datareader ADD MEMBER [nombre-app].
        </Notice>
      )}
      <Field
        label={sp ? 'Secreto del service principal' : 'Contrasena'}
        error={errors.secret}
        hint={secretHint(savedSecretUsable)}
      >
        {(control) => (
          <PasswordInput
            {...control}
            value={form.secret}
            placeholder={savedSecretUsable ? '********' : ''}
            onChange={text('secret')}
          />
        )}
      </Field>
    </div>
  );
}

/** Formulario MySQL / MariaDB / Azure Database for MySQL. */
export function MysqlFields({
  form,
  errors,
  savedSecretUsable,
  locked,
  onChange,
}: {
  readonly form: MysqlForm;
  readonly errors: Errors;
  readonly savedSecretUsable: boolean;
  readonly locked: boolean;
  readonly onChange: (form: MysqlForm) => void;
}) {
  const text = (key: keyof MysqlForm) => (event: { target: { value: string } }) => {
    onChange({ ...form, [key]: event.target.value });
  };
  return (
    <div className="flex flex-col gap-3">
      <Notice tone="info">
        Use un usuario con solo SELECT. La sesion se abre en modo de solo lectura.
      </Notice>
      <fieldset disabled={locked} className="min-w-0">
        <legend className="sr-only">Datos del servidor</legend>
        <div className="grid gap-3 sm:grid-cols-6">
          <ServerFields
            host={form.host}
            port={form.port}
            database={form.database}
            errors={errors}
            hostHint="Por ejemplo miservidor.mysql.database.azure.com"
            onText={(key, value) => {
              onChange({ ...form, [key]: value });
            }}
          />
          <Field label="Usuario" className="sm:col-span-3" error={errors.user}>
            {(control) => (
              <Input
                {...control}
                value={form.user}
                autoComplete="off"
                spellCheck={false}
                onChange={text('user')}
              />
            )}
          </Field>
          <Field label="Cifrado de la conexion" className="sm:col-span-6" error={errors.sslMode}>
            {(control) => (
              <Select {...control} value={form.sslMode} onChange={text('sslMode')}>
                {MYSQL_SSL_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value}>
                    {mode.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </fieldset>
      <Field label="Contrasena" error={errors.password} hint={secretHint(savedSecretUsable)}>
        {(control) => (
          <PasswordInput
            {...control}
            value={form.password}
            placeholder={savedSecretUsable ? '********' : ''}
            onChange={text('password')}
          />
        )}
      </Field>
    </div>
  );
}
